import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, Container, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import UpdateIcon from "@mui/icons-material/Update";
import { useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import LegalTableOfContents from "./LegalTableOfContents";
import RelatedPolicies from "./RelatedPolicies";
import { HEADER_OFFSET_PX, LegalBlock, LegalSection, legalAccent } from "./LegalBlocks";

// Animated scrolling is opt-out rather than global: putting `scroll-behavior: smooth` in
// index.css would also animate the window.scrollTo(0, 0) calls that Header and Footer make
// on every navigation.
const scrollBehavior = () => {
  const reduced =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduced ? "auto" : "smooth";
};

/**
 * Shared shell for the Privacy Policy, Cookie Policy and Terms of Service. Takes a
 * structured document (see e.g. src/pages/privacypolicy/privacyPolicyData.js) and renders
 * the header band, the section index and the body.
 */
const LegalPage = ({ policy }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const accent = legalAccent(theme);
  const location = useLocation();
  const { sections } = policy;

  const [activeId, setActiveId] = useState(sections[0] ? sections[0].id : null);

  // Scroll-spy: a heading counts as current once it has passed under the AppBar but before
  // it leaves the top third of the viewport.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const headings = sections
      .map((section) => window.document.getElementById(section.id))
      .filter(Boolean);

    if (headings.length === 0) {
      return undefined;
    }

    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => visible.set(entry.target.id, entry.isIntersecting));
        const current = sections.find((section) => visible.get(section.id));
        if (current) {
          setActiveId(current.id);
        }
      },
      { rootMargin: `-${HEADER_OFFSET_PX}px 0px -70% 0px`, threshold: 0 }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [sections]);

  // Honour a deep link such as /privacypolicy#your-rights. Runs once on mount only —
  // reacting to location.hash would fight the smooth scroll started by handleNavigate.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) {
      return undefined;
    }

    const id = hash.slice(1);
    let cancelled = false;
    let userTookOver = false;

    // Intent, not position: a reflow moves window.scrollY on its own, so comparing scroll
    // offsets would read "the user scrolled" when nothing of the sort happened and abandon
    // the target half-way behind the AppBar.
    const yieldToUser = () => {
      userTookOver = true;
    };
    const interactions = ["wheel", "touchstart", "keydown", "mousedown"];
    interactions.forEach((event) =>
      window.addEventListener(event, yieldToUser, { passive: true, once: true })
    );

    const align = () => {
      if (cancelled || userTookOver) {
        return;
      }
      const target = window.document.getElementById(id);
      if (!target) {
        return;
      }
      target.scrollIntoView({ behavior: "auto", block: "start" });
      setActiveId(id);
    };

    const frame = window.requestAnimationFrame(align);
    // The web font arrives after first paint and reflows the whole document, dragging the
    // target out from under a single early scroll. Re-align when it settles, plus timed
    // backstops for the case where the font was already cached and `ready` resolves before
    // layout is final.
    if (window.document.fonts && window.document.fonts.ready) {
      window.document.fonts.ready.then(align);
    }
    const timers = [200, 600].map((delay) => window.setTimeout(align, delay));

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      timers.forEach(window.clearTimeout);
      interactions.forEach((event) => window.removeEventListener(event, yieldToUser));
    };
  }, []);

  const handleNavigate = useCallback((event, id) => {
    event.preventDefault();
    const target = window.document.getElementById(id);
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
    setActiveId(id);
    // Keep react-router's own history state intact — replacing it with null breaks the
    // router's back/forward bookkeeping.
    window.history.replaceState(window.history.state, "", `#${id}`);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
  };

  return (
    <Box component="main" sx={{ backgroundColor: colors.background[200] }}>
      {/* Document header */}
      <Box
        sx={{
          backgroundColor: colors.background[300],
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          {policy.eyebrow ? (
            <Typography
              variant="h6"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: accent,
                mb: 1,
              }}
            >
              {policy.eyebrow}
            </Typography>
          ) : null}

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2rem", md: "2.75rem" },
              color: theme.palette.text.primary,
              mb: policy.summary ? 1.5 : 2.5,
            }}
          >
            {policy.title}
          </Typography>

          {policy.summary ? (
            <Typography
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                lineHeight: 1.7,
                color: theme.palette.text.secondary,
                maxWidth: 720,
                mb: 2.5,
              }}
            >
              {policy.summary}
            </Typography>
          ) : null}

          {/* useFlexGap because Stack's default margin-based spacing breaks once the chips
              wrap onto a second line on narrow screens. */}
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
            {policy.effectiveDate ? (
              <Chip
                size="small"
                variant="outlined"
                icon={<EventAvailableIcon />}
                label={`Effective ${policy.effectiveDate}`}
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  "& .MuiChip-icon": { color: accent },
                }}
              />
            ) : null}
            {policy.lastUpdated ? (
              <Chip
                size="small"
                variant="outlined"
                icon={<UpdateIcon />}
                label={`Last updated ${policy.lastUpdated}`}
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  "& .MuiChip-icon": { color: accent },
                }}
              />
            ) : null}
          </Stack>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 0, md: 6 }}>
          <Grid item xs={12} md={3}>
            <LegalTableOfContents
              sections={sections}
              activeId={activeId}
              onNavigate={handleNavigate}
            />
          </Grid>

          <Grid item xs={12} md={9}>
            {/* The bottom padding is not decoration: without slack below the last section
                the page runs out of scroll and a deep link to it lands behind the AppBar. */}
            <Box sx={{ maxWidth: 800, pb: { xs: 4, md: 8 } }}>
              {policy.intro && policy.intro.length > 0 ? (
                <>
                  <Stack spacing={2.5} sx={{ mb: { xs: 5, md: 6 } }}>
                    {policy.intro.map((block, index) => (
                      <LegalBlock key={index} block={block} />
                    ))}
                  </Stack>
                  <Divider sx={{ mb: { xs: 5, md: 6 } }} />
                </>
              ) : null}

              {sections.map((section, index) => (
                <LegalSection key={section.id} section={section} number={index + 1} />
              ))}

              <Button
                onClick={handleBackToTop}
                startIcon={<ArrowUpwardIcon />}
                sx={{ color: accent, px: 0 }}
              >
                Back to top
              </Button>

              <RelatedPolicies currentPath={location.pathname} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LegalPage;
